import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useOrganisation } from '@/hooks/useOrganisation';

interface InternalStaffContextType {
  isInternal: boolean;
  internalRole: 'global_admin' | 'staff' | 'guest' | null;
  isGlobalAdmin: boolean;
  isDemoMode: boolean;
  setDemoMode: (value: boolean) => void;
}

const InternalStaffContext = createContext<InternalStaffContextType>({
  isInternal: false,
  internalRole: null,
  isGlobalAdmin: false,
  isDemoMode: false,
  setDemoMode: () => {},
});

export const InternalStaffProvider = ({ children }: { children: ReactNode }) => {
  const { currentOrg, currentInternalRole, loading } = useOrganisation();
  const [isDemoMode, setIsDemoMode] = useState(() => {
    return sessionStorage.getItem('wellebit-demo-mode') === 'true';
  });

  const isInternal = currentOrg?.is_internal ?? false;
  const internalRole = isInternal ? (currentInternalRole as 'global_admin' | 'staff' | 'guest' | null) : null;
  const isGlobalAdmin = internalRole === 'global_admin';

  const setDemoModeWithStorage = (value: boolean) => {
    setIsDemoMode(value);
    sessionStorage.setItem('wellebit-demo-mode', value ? 'true' : 'false');
  };

  useEffect(() => {
    if (!loading && !isInternal && isDemoMode) {
      setDemoModeWithStorage(false);
    }
    // Guests can only use demo mode
    if (!loading && isInternal && internalRole === 'guest' && !isDemoMode) {
      setDemoModeWithStorage(true);
    }
  }, [isInternal, loading, internalRole]);

  return (
    <InternalStaffContext.Provider
      value={{
        isInternal,
        internalRole,
        isGlobalAdmin,
        isDemoMode: isInternal && isDemoMode,
        setDemoMode: setDemoModeWithStorage,
      }}
    >
      {children}
    </InternalStaffContext.Provider>
  );
};

export const useInternalStaffContext = () => useContext(InternalStaffContext);
